import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apis from '#store/api';
import { DataApiResponse, DataCollectionApiResponse } from './types';

export const SUPER_ADMINS_KEY = 'superAdmins';
export const SUPER_ADMINS_QUERY_KEYS = {
  LIST: 'list',
  INFINITE: 'infinite'
} as const;

export interface SuperAdmin {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
}

export interface CreateSuperAdminDto {
  firstName?: string;
  lastName?: string;
  email?: string;
  password?: string;
  phoneNumber?: string;
}

interface CreateSuperAdminParams {
  superAdmin: CreateSuperAdminDto;
}

interface UpdateSuperAdminParams {
  id: string;
  superAdmin: CreateSuperAdminDto;
}

interface DeleteSuperAdminParams {
  id: string;
}

interface GetSuperAdminByIdParams {
  id: string;
}

interface GetSuperAdminsParams {
  skip?: number;
  take: number;
}

interface ResetPasswordSuperAdminParams {
  id: string;
}

export const useSuperAdmins = (params: GetSuperAdminsParams) => {
  return useQuery({
    queryKey: [SUPER_ADMINS_KEY, SUPER_ADMINS_QUERY_KEYS.LIST, params],
    queryFn: async () => {
      const response = await apis.superAdmins.getSuperAdminsList({ params });
      return response.data as DataCollectionApiResponse<SuperAdmin>;
    },
    refetchOnMount: 'always',
    refetchOnWindowFocus: false
  });
};


export const useSuperAdminById = (params: GetSuperAdminByIdParams) => {
  return useQuery({
    queryKey: [SUPER_ADMINS_KEY, params],
    queryFn: async () => {
      const response = await apis.superAdmins.getSuperAdminById({ data: params });
      return response.data as DataApiResponse<SuperAdmin>;
    },
  });
};

export const useCreateSuperAdmin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: CreateSuperAdminParams) => {
      const response = await apis.superAdmins.createSuperAdmin({ data: params });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SUPER_ADMINS_KEY] });
    },
  });
};

export const useDeleteSuperAdmin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: DeleteSuperAdminParams) => {
      const response = await apis.superAdmins.deleteSuperAdmin({ data: params });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SUPER_ADMINS_KEY] });
    }
  });
};

export const useResetPasswordSuperAdmin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: ResetPasswordSuperAdminParams) => {
      const response = await apis.superAdmins.resetPasswordSuperAdmin({ data: params });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SUPER_ADMINS_KEY] });
    }
  });
};

export const useUpdateSuperAdmin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: UpdateSuperAdminParams) => {
      const response = await apis.superAdmins.updateSuperAdmin({
        data: params, 
      });
      return response.data as DataApiResponse<unknown>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SUPER_ADMINS_KEY] });
    },
  });
};
